import React, { useState, useRef, useEffect } from 'react'
import moment from 'moment'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { Button } from 'antd'

import calendarIcon from '@/assets/images/icon-calendar-24.svg'
import leftIcon from '@/assets/images/icon-calendar-chevron-left.svg'
import rightIcon from '@/assets/images/icon-calendar-chevron-right.svg'

import styles from './style.module.scss'

const DateInput = ({
  value,
  onChange,
  placeholder = 'YYYY-MM-DD',
  format = 'YYYY-MM-DD',
  disabled = false,
  className,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef(null)
  const calendarRef = useRef(null)
  const { t } = useTranslation()

  const displayValue = value ? moment(value).format(format) : ''

  // 언어에 따른 요일 배열 생성
  const weekDays = [
    t('record.select.expiredDate.weekdays.sunday'),
    t('record.select.expiredDate.weekdays.monday'),
    t('record.select.expiredDate.weekdays.tuesday'),
    t('record.select.expiredDate.weekdays.wednesday'),
    t('record.select.expiredDate.weekdays.thursday'),
    t('record.select.expiredDate.weekdays.friday'),
    t('record.select.expiredDate.weekdays.saturday'),
  ]

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        // 버튼이나 캘린더 내부 요소 클릭이 아닐 때만 닫기
        const isButtonClick = event.target.closest('button') !== null
        const isCalendarClick = calendarRef.current.contains(event.target)

        if (!isButtonClick && !isCalendarClick) {
          setIsOpen(false)
        }
      }
    }

    if (isOpen) {
      // 약간의 지연을 두어 날짜 선택 클릭이 먼저 처리되도록
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 100)

      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }

    return () => {}
  }, [isOpen])

  const handleWrapperClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleDateSelect = (date) => {
    onChange?.(date)
    setIsOpen(false)
  }

  const handleTodayClick = () => {
    const today = moment().toDate()
    handleDateSelect(today)
  }

  const [displayMonth, setDisplayMonth] = useState(() => {
    if (value) {
      const date = moment(value)
      return { year: date.year(), month: date.month() }
    }
    const now = moment()
    return { year: now.year(), month: now.month() }
  })

  useEffect(() => {
    if (value) {
      const date = moment(value)
      setDisplayMonth({ year: date.year(), month: date.month() })
    }
  }, [value])

  const handlePrevMonth = (e) => {
    e?.stopPropagation()
    const newMonth = moment([displayMonth.year, displayMonth.month]).subtract(1, 'month')
    setDisplayMonth({ year: newMonth.year(), month: newMonth.month() })
  }

  const handleNextMonth = (e) => {
    e?.stopPropagation()
    const newMonth = moment([displayMonth.year, displayMonth.month]).add(1, 'month')
    setDisplayMonth({ year: newMonth.year(), month: newMonth.month() })
  }

  const renderCalendar = () => {
    if (!isOpen) return null

    const { year, month } = displayMonth

    const handleBackdropClick = (e) => {
      if (e.target === e.currentTarget) {
        setIsOpen(false)
      }
    }

    const startOfMonth = moment([year, month, 1])
    const endOfMonth = moment([year, month]).endOf('month')
    const startDate = startOfMonth.clone().startOf('week')
    const endDate = endOfMonth.clone().endOf('week')

    const days = []
    const day = startDate.clone()

    while (day.isSameOrBefore(endDate, 'day')) {
      days.push(day.clone())
      day.add(1, 'day')
    }

    const monthNumber = month + 1
    const monthName = t(`record.select.expiredDate.months.${monthNumber}`)
    const monthYearText = t('record.select.expiredDate.monthFormat', {
      year,
      month: monthName,
    })

    return (
      <>
        <div
          className={styles.backdrop}
          onClick={handleBackdropClick}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsOpen(false)
            }
          }}
          role="button"
          tabIndex={-1}
          aria-label="Close calendar"
        />
        <div ref={calendarRef} className={styles.calendarDropdown}>
          <div className={styles.calendarHeader}>
            <span className={styles.monthYear}>{monthYearText}</span>
            <Button
              type="text"
              className={styles.navButton}
              onClick={handlePrevMonth}
              icon={<img src={leftIcon} alt="Previous" />}
            />
            <Button
              type="text"
              className={styles.navButton}
              onClick={handleNextMonth}
              icon={<img src={rightIcon} alt="Next" />}
            />
          </div>
          <div className={styles.weekDays}>
            {weekDays.map((dayName) => (
              <div key={dayName} className={styles.weekDay}>
                {dayName}
              </div>
            ))}
          </div>
          <div className={styles.days}>
            {Array.from({ length: Math.ceil(days.length / 7) }, (_, weekIndex) => (
              <div key={weekIndex} className={styles.weekRow}>
                {days.slice(weekIndex * 7, (weekIndex + 1) * 7).map((dayItem) => {
                  const isCurrentMonth = dayItem.month() === month
                  const isSelected = value && dayItem.isSame(moment(value), 'day')
                  const isBeforeToday = dayItem.isBefore(moment(), 'day')
                  const dayKey = dayItem.format('YYYY-MM-DD')

                  return (
                    <button
                      key={dayKey}
                      type="button"
                      className={classNames(styles.day, {
                        [styles.otherMonth]: !isCurrentMonth,
                        [styles.selected]: isSelected,
                        [styles.disabled]: isBeforeToday,
                      })}
                      onClick={() => handleDateSelect(dayItem.toDate())}
                      disabled={!isCurrentMonth || isBeforeToday}
                    >
                      {dayItem.format('D')}
                    </button>
                  )
                })}
              </div>
            ))}
          </div>
          <div className={styles.calendarFooter}>
            <button type="button" className={styles.todayButton} onClick={handleTodayClick}>
              {t('record.select.expiredDate.today')}
            </button>
          </div>
        </div>
      </>
    )
  }

  return (
    <div
      ref={wrapperRef}
      className={classNames(styles.dateInputWrapper, className, { [styles.disabled]: disabled })}
      onClick={handleWrapperClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          setIsOpen(!isOpen)
        }
      }}
      {...props}
    >
      <div className={styles.dateInput}>
        {displayValue || <span className={styles.placeholder}>{placeholder}</span>}
      </div>
      <div className={styles.calendarButton}>
        <img src={calendarIcon} alt="Calendar" className={styles.calendarIcon} />
      </div>
      {renderCalendar()}
    </div>
  )
}

export default DateInput
